import { type ComponentType, memo, useCallback, useEffect, useState } from 'react';

import { WorkspaceAgentGuidedVariantSettingsActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsActions';
import { WorkspaceAgentGuidedVariantWatermarkMarginField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantWatermarkMarginField';
import { WorkspaceAgentGuidedVariantWatermarkNumberField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantWatermarkNumberField';
import { WorkspaceAgentGuidedVariantWatermarkPlacementField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantWatermarkPlacementField';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import { buildWorkspaceAgentWatermarkComposerContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentWatermarkComposerContent';
import { type WorkspaceAgentGuidedVariantImagePreviewSelection } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePreviewSelection';
import {
  buildWorkspaceAgentWatermarkDescription,
  WORKSPACE_AGENT_WATERMARK_DEFAULTS,
  WORKSPACE_AGENT_WATERMARK_LIMITS,
  type WorkspaceAgentWatermarkImage,
  type WorkspaceAgentWatermarkMarginUnit,
  type WorkspaceAgentWatermarkPlacementId,
  type WorkspaceAgentWatermarkSettings,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentWatermark';

export type WorkspaceAgentGuidedVariantWatermarkImageControlProps = {
  onSelect: (image: WorkspaceAgentWatermarkImage | null) => void;
  selected: WorkspaceAgentWatermarkImage | null;
};

export type WorkspaceAgentGuidedVariantWatermarkSettingsProps = {
  ImageControl: ComponentType<WorkspaceAgentGuidedVariantWatermarkImageControlProps>;
  /** Shorter side of the smallest selected image. Null until one of those images reports a size. */
  marginPxMax: number | null;
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void;
  watermarkSelection: WorkspaceAgentGuidedVariantImagePreviewSelection;
};

export const WorkspaceAgentGuidedVariantWatermarkSettings = memo(
  ({
    ImageControl,
    marginPxMax,
    onGenerate,
    watermarkSelection,
  }: WorkspaceAgentGuidedVariantWatermarkSettingsProps) => {
    const [settings, setSettings] = useState<WorkspaceAgentWatermarkSettings>(WORKSPACE_AGENT_WATERMARK_DEFAULTS);
    const [watermarkImage, setWatermarkImage] = useState<WorkspaceAgentWatermarkImage | null>(null);
    const isGenerateDisabled = !watermarkImage || watermarkSelection.imageCount < 1;

    const handlePlacementChange = useCallback((placement: WorkspaceAgentWatermarkPlacementId) => {
      setSettings((value) => ({ ...value, placement }));
    }, []);

    const handleSizeChange = useCallback((sizePercent: number) => {
      setSettings((value) => ({ ...value, sizePercent }));
    }, []);

    const handleOpacityChange = useCallback((opacity: number) => {
      setSettings((value) => ({ ...value, opacity }));
    }, []);

    const handleMarginChange = useCallback((margin: number) => {
      setSettings((value) => ({ ...value, margin }));
    }, []);

    const handleMarginUnitChange = useCallback((marginUnit: WorkspaceAgentWatermarkMarginUnit) => {
      setSettings((value) => ({ ...value, marginUnit }));
    }, []);

    useEffect(() => {
      if (marginPxMax === null) {
        return;
      }

      setSettings((value) => {
        if (value.marginUnit !== 'px' || value.margin <= marginPxMax) {
          return value;
        }

        return { ...value, margin: marginPxMax };
      });
    }, [marginPxMax]);

    const handleGenerate = useCallback(() => {
      if (!watermarkImage || watermarkSelection.imageCount < 1) {
        return;
      }

      onGenerate({
        allowsJpeg: true,
        kind: 'jsonContent',
        build: (destination, output) =>
          buildWorkspaceAgentWatermarkComposerContent({
            destination,
            image: watermarkImage,
            imageCount: watermarkSelection.imageCount,
            output,
            settings,
            skippedFileCount: watermarkSelection.skippedFileCount,
            skippedVideoCount: watermarkSelection.skippedVideoCount,
          }),
        structuredDisplay: {
          description: buildWorkspaceAgentWatermarkDescription({
            settings,
            skippedFileCount: watermarkSelection.skippedFileCount,
            skippedVideoCount: watermarkSelection.skippedVideoCount,
          }),
          icon: 'stamp',
          title: 'Watermark',
        },
      });
    }, [onGenerate, settings, watermarkImage, watermarkSelection]);

    return (
      <div className="flex flex-col gap-3" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_SETTINGS">
        <div className="flex flex-col gap-1">
          <p className="font-medium ads:text-primary text-12 text-gray-12">Select watermark</p>
          <ImageControl onSelect={setWatermarkImage} selected={watermarkImage} />
        </div>
        <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] items-start gap-x-4 gap-y-3">
          <WorkspaceAgentGuidedVariantWatermarkPlacementField
            onChange={handlePlacementChange}
            placement={settings.placement}
          />
          <WorkspaceAgentGuidedVariantWatermarkNumberField
            label="Size"
            max={WORKSPACE_AGENT_WATERMARK_LIMITS.sizePercentMax}
            min={WORKSPACE_AGENT_WATERMARK_LIMITS.sizePercentMin}
            onChange={handleSizeChange}
            suffix="%"
            testId="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_SIZE"
            value={settings.sizePercent}
          />
          <WorkspaceAgentGuidedVariantWatermarkNumberField
            label="Opacity"
            max={WORKSPACE_AGENT_WATERMARK_LIMITS.opacityMax}
            min={0}
            onChange={handleOpacityChange}
            suffix="%"
            testId="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_OPACITY"
            value={settings.opacity}
          />
          <div
            aria-hidden={settings.placement === 'center'}
            className={settings.placement === 'center' ? 'invisible col-start-2' : 'col-start-2'}
          >
            <WorkspaceAgentGuidedVariantWatermarkMarginField
              disabled={settings.placement === 'center'}
              margin={settings.margin}
              marginPxMax={marginPxMax}
              marginUnit={settings.marginUnit}
              onMarginChange={handleMarginChange}
              onMarginUnitChange={handleMarginUnitChange}
            />
          </div>
        </div>
        <WorkspaceAgentGuidedVariantSettingsActions
          generateLabel={`Generate ${watermarkSelection.imageCount.toLocaleString()}`}
          isGenerateDisabled={isGenerateDisabled}
          onGenerate={handleGenerate}
        />
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantWatermarkSettings.displayName = 'WorkspaceAgentGuidedVariantWatermarkSettings';
